import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, IconButton, TextField, InputAdornment, Tooltip, Chip, Snackbar } from '@mui/material';
import {
  Psychology as PsychologyIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  OpenInFull as ExpandIcon,
  CloseFullscreen as CollapseIcon,
  AdminPanelSettings as AdminIcon,
  LocalHospital as DoctorIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { naraKeystroke } from './emr/NaraTerminal';
import { subscribeNaraContext, buildContextString } from './naraChatContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

const mono = {
  fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", "Courier New", monospace',
};

// Panel size configs
const PANEL_SIZES = {
  compact:    { width: 340, height: 420 },
  expanded:   { width: 560, height: 520 },
  fullscreen: { width: 'calc(100vw - 48px)', height: 'calc(100vh - 48px)' },
};

// Mode theme colors
const MODE_THEME = {
  coordinador: {
    primary: '#0891b2', secondary: '#0e7490',
    gradient: 'linear-gradient(135deg, #0891b2, #0e7490)',
    shadow: 'rgba(8, 145, 178, 0.5)', shadowHover: 'rgba(8, 145, 178, 0.7)',
    border: 'rgba(8, 145, 178, 0.25)',
    barGradient: 'linear-gradient(90deg, #0891b2 0%, #0e7490 50%, transparent 100%)',
    chipBg: 'rgba(8, 145, 178, 0.15)', chipBorder: 'rgba(8, 145, 178, 0.3)',
    chipColor: '#22d3ee', label: 'Coordinador',
  },
  admin: {
    primary: '#8b5cf6', secondary: '#6366f1',
    gradient: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
    shadow: 'rgba(139, 92, 246, 0.5)', shadowHover: 'rgba(139, 92, 246, 0.7)',
    border: 'rgba(139, 92, 246, 0.25)',
    barGradient: 'linear-gradient(90deg, #8b5cf6 0%, #6366f1 50%, transparent 100%)',
    chipBg: 'rgba(139, 92, 246, 0.15)', chipBorder: 'rgba(139, 92, 246, 0.3)',
    chipColor: '#a78bfa', label: 'Admin',
  }
};

// Page context mapping
const PAGE_CONTEXT_MAP = {
  '/dashboard': 'Dashboard principal',
  '/booking': 'Paquetes y Destinos',
  '/paquetes': 'Paquetes y Destinos',
  '/salidas': 'Salidas Programadas',
  '/reservaciones': 'Reservaciones',
  '/conversaciones': 'Dashboard de Conversaciones WhatsApp',
  '/analytics': 'Analiticas y reportes',
  '/smart-flows': 'Configuracion de Chatbots (Smart Flows)',
  '/consumo-tokens': 'Consumo de Servicios',
  '/suscripcion': 'Suscripcion y planes',
  '/admin/usuarios': 'Admin: Usuarios',
  '/admin/planes': 'Admin: Gestion de Planes',
  '/admin/servicios': 'Admin: Servicios',
  '/admin/db': 'Admin: Base de Datos',
};

// Smart suggestions based on page + entity context
const PAGE_SUGGESTIONS = {
  '/dashboard': ['Salidas de hoy', 'Reservaciones recientes', 'Resumen semanal'],
  '/salidas': ['Ocupacion del dia', 'Salidas con cupos', 'Salidas de esta semana'],
  '/reservaciones': ['Reservaciones de hoy', 'Pendientes de pago', 'Cancelaciones recientes'],
  '/conversaciones': ['Conversaciones de hoy', 'Escalaciones pendientes', 'Temas frecuentes'],
  '/analytics': ['Metricas del mes', 'Chatbot mas activo', 'Tendencia de uso'],
  '/smart-flows': ['Chatbots activos', 'KB mas grande', 'Tokens por chatbot'],
  '/admin/db': ['Stats del sistema', 'Empresas activas', 'Usuarios registrados'],
};

const ENTITY_SUGGESTIONS = {
  reservacion: (d) => [
    `Detalles de esta reservacion`,
    `Estado de pago`,
    `Viajeros registrados`,
  ],
  salida: (d) => [
    `Ocupacion de esta salida`,
    `Reservaciones asociadas`,
    `Estado del clima`,
  ],
  chatbot: (d) => [
    `Estadisticas de este chatbot`,
    `Conversaciones recientes`,
    `Tokens consumidos`,
  ],
};

const DEFAULT_SUGGESTIONS = ['Salidas de hoy', 'Reservaciones pendientes', 'Ayuda'];

// Typewriter for NARA messages
const ChatTypewriter = ({ text, onDone }) => {
  const [displayed, setDisplayed] = useState('');
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    setDisplayed('');
    if (!text) return;
    let i = 0;
    const timer = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      const ch = text[i - 1];
      if (ch && ch !== ' ') naraKeystroke();
      if (i >= text.length) {
        clearInterval(timer);
        if (onDoneRef.current) onDoneRef.current();
      }
    }, 18);
    return () => clearInterval(timer);
  }, [text]);

  return (
    <span>
      {displayed}
      {displayed.length < (text || '').length && (
        <Box component="span" sx={{
          '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0 } },
          animation: 'blink 0.8s step-end infinite',
          color: '#10b981', fontWeight: 700
        }}>|</Box>
      )}
    </span>
  );
};

// Markdown renderer with navigable links
const MarkdownMessage = ({ content, theme, onNavigate }) => (
  <ReactMarkdown
    components={{
      p: ({ children }) => (
        <Typography component="span" sx={{ ...mono, fontSize: 11.5, color: '#e2e8f0', lineHeight: 1.6, display: 'block', mb: 0.5 }}>
          {children}
        </Typography>
      ),
      strong: ({ children }) => (
        <Box component="span" sx={{ color: theme.chipColor, fontWeight: 700 }}>{children}</Box>
      ),
      em: ({ children }) => (
        <Box component="span" sx={{ fontStyle: 'italic', color: '#94a3b8' }}>{children}</Box>
      ),
      ul: ({ children }) => (
        <Box component="ul" sx={{ pl: 2, my: 0.5, '& li': { ...mono, fontSize: 11, color: '#cbd5e1', lineHeight: 1.6, mb: 0.25 } }}>
          {children}
        </Box>
      ),
      ol: ({ children }) => (
        <Box component="ol" sx={{ pl: 2, my: 0.5, '& li': { ...mono, fontSize: 11, color: '#cbd5e1', lineHeight: 1.6, mb: 0.25 } }}>
          {children}
        </Box>
      ),
      code: ({ inline, children }) => inline ? (
        <Box component="code" sx={{
          bgcolor: 'rgba(255,255,255,0.08)', px: 0.5, py: 0.15, borderRadius: 0.5,
          fontSize: 10.5, color: '#fbbf24', ...mono
        }}>{children}</Box>
      ) : (
        <Box component="pre" sx={{
          bgcolor: 'rgba(0,0,0,0.3)', p: 1, borderRadius: 1, my: 0.5,
          overflowX: 'auto', border: '1px solid rgba(255,255,255,0.06)',
          '& code': { ...mono, fontSize: 10.5, color: '#e2e8f0' }
        }}>
          <code>{children}</code>
        </Box>
      ),
      a: ({ href, children }) => {
        // Internal navigation links (start with /)
        const isInternal = href && href.startsWith('/');
        return (
          <Box
            component="span"
            onClick={(e) => {
              e.preventDefault();
              if (isInternal && onNavigate) onNavigate(href);
            }}
            sx={{
              color: theme.chipColor, cursor: isInternal ? 'pointer' : 'default',
              textDecoration: 'underline', textDecorationColor: `${theme.chipColor}44`,
              fontWeight: 600,
              '&:hover': isInternal ? { color: '#fff', textDecorationColor: '#fff' } : {},
            }}
          >
            {children}
          </Box>
        );
      },
      table: ({ children }) => (
        <Box component="table" sx={{
          width: '100%', my: 0.5, borderCollapse: 'collapse',
          '& th': { ...mono, fontSize: 10, color: theme.chipColor, textAlign: 'left', px: 1, py: 0.5, borderBottom: '1px solid rgba(255,255,255,0.15)', fontWeight: 700 },
          '& td': { ...mono, fontSize: 10.5, color: '#cbd5e1', px: 1, py: 0.4, borderBottom: '1px solid rgba(255,255,255,0.06)' },
          '& tr:hover td': { bgcolor: 'rgba(255,255,255,0.03)' }
        }}>
          {children}
        </Box>
      ),
      h1: ({ children }) => <Typography sx={{ ...mono, fontSize: 14, fontWeight: 700, color: '#f1f5f9', mb: 0.5 }}>{children}</Typography>,
      h2: ({ children }) => <Typography sx={{ ...mono, fontSize: 13, fontWeight: 700, color: '#e2e8f0', mb: 0.5 }}>{children}</Typography>,
      h3: ({ children }) => <Typography sx={{ ...mono, fontSize: 12, fontWeight: 600, color: '#cbd5e1', mb: 0.5 }}>{children}</Typography>,
      hr: () => <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', my: 1 }} />,
    }}
  >
    {content}
  </ReactMarkdown>
);

const NaraChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [typingIdx, setTypingIdx] = useState(-1);
  const [panelSize, setPanelSize] = useState('compact');
  const [voiceMode, setVoiceMode] = useState(false);
  const [voiceTranscribing, setVoiceTranscribing] = useState(false);
  const [voiceError, setVoiceError] = useState(null);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [hoveredMsg, setHoveredMsg] = useState(-1);
  const [entityContext, setEntityContext] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const voiceActiveRef = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();
  const mode = isAdmin ? 'admin' : 'coordinador';
  const theme = MODE_THEME[mode];

  // Subscribe to entity context changes from dashboard components
  useEffect(() => {
    const unsub = subscribeNaraContext((ctx) => {
      setEntityContext(ctx);
    });
    return unsub;
  }, []);

  // Listen for "nara-ask" events from AskNara buttons
  useEffect(() => {
    const handler = (e) => {
      const question = e.detail?.question;
      if (question) {
        if (!open) setOpen(true);
        // Slight delay to let panel animate open
        setTimeout(() => sendMessage(question), open ? 50 : 400);
      }
    };
    window.addEventListener('nara-ask', handler);
    return () => window.removeEventListener('nara-ask', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId, loading, messages]);

  // Get page context from current route
  const getPageContext = useCallback(() => {
    const path = location.pathname;
    if (PAGE_CONTEXT_MAP[path]) return PAGE_CONTEXT_MAP[path];
    for (const [route, label] of Object.entries(PAGE_CONTEXT_MAP)) {
      if (path.startsWith(route) && route !== '/') return label;
    }
    return null;
  }, [location.pathname]);

  // Build full context string (page + entity)
  const getFullContext = useCallback(() => {
    const parts = [];
    const pageCtx = getPageContext();
    if (pageCtx) parts.push(`Pagina: ${pageCtx}`);
    const entityStr = buildContextString(entityContext);
    if (entityStr) parts.push(entityStr);
    return parts.length > 0 ? parts.join('. ') : null;
  }, [getPageContext, entityContext]);

  // Get smart suggestions based on page + entity
  const getSuggestions = useCallback(() => {
    // If entity context exists, use entity-specific suggestions
    if (entityContext) {
      const fn = ENTITY_SUGGESTIONS[entityContext.type];
      if (fn) return fn(entityContext.data);
    }
    const path = location.pathname;
    for (const [route, suggestions] of Object.entries(PAGE_SUGGESTIONS)) {
      if (path.startsWith(route)) return suggestions;
    }
    return DEFAULT_SUGGESTIONS;
  }, [location.pathname, entityContext]);

  // Auth check
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (u) {
        let id = localStorage.getItem('user_id');
        let role = localStorage.getItem('user_role');
        let companyId = localStorage.getItem('user_company_id');
        if (!id || !role || !companyId) {
          try {
            const res = await fetch(`${API_URL}/users/by_uid/${u.uid}`);
            const data = await res.json();
            if (data?.id) { id = data.id; localStorage.setItem('user_id', id); }
            if (data?.role) { role = data.role; localStorage.setItem('user_role', role); }
            if (data?.company_id) { companyId = String(data.company_id); localStorage.setItem('user_company_id', companyId); }
          } catch (err) { /* silent */ }
        }
        setUserId(id);
        setIsAdmin(companyId === '4' || role === 'superadmin');
      } else {
        setUserId(null);
        setIsAdmin(false);
      }
    });
    return () => unsub();
  }, []);

  // Keyboard shortcut: Ctrl+Shift+N
  useEffect(() => {
    const handleKD = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'N') {
        e.preventDefault();
        if (open) handleClose();
        else handleOpen();
      }
    };
    window.addEventListener('keydown', handleKD);
    return () => window.removeEventListener('keydown', handleKD);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, messages.length, isAdmin]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typingIdx]);

  // Focus input when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
    if (messages.length === 0) {
      const entityStr = buildContextString(entityContext);
      const pageCtx = getPageContext();
      let ctxHint = '';
      if (entityStr) ctxHint = ` ${entityStr}.`;
      else if (pageCtx) ctxHint = ` Veo que estas en: ${pageCtx}.`;

      const welcomeMsg = isAdmin
        ? `Hola. Soy NARA en modo administrador.${ctxHint} Puedo consultar reservaciones, salidas, paquetes, ocupación, analytics y configuración del sistema. ¿En qué le ayudo?`
        : `Hola. Soy NARA, su asistente de turismo.${ctxHint} Puede preguntarme sobre reservaciones, salidas, destinos, ocupación o lo que necesite.`;
      setMessages([{ role: 'assistant', content: welcomeMsg }]);
      setTypingIdx(0);
    }
  };

  const cyclePanelSize = () => {
    setPanelSize(prev => prev === 'compact' ? 'expanded' : prev === 'expanded' ? 'fullscreen' : 'compact');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => setCopySnackbar(true)).catch(() => {});
  };

  const handleNavigate = (path) => {
    if (path) navigate(path);
  };

  const sendMessage = async (msg) => {
    if (!msg || loading || !userId) return;

    const newMessages = [...messages, { role: 'user', content: msg }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const historialPrevio = messages.slice(-10).map(m => ({ role: m.role, content: m.content }));
      const fullContext = getFullContext();

      const res = await fetch(`${API_URL}/emr/ia/nara-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({
          mensaje: msg,
          historial: historialPrevio,
          contexto_pagina: fullContext
        })
      });

      const data = await res.json();
      if (res.ok && data.respuesta) {
        const updated = [...newMessages, { role: 'assistant', content: data.respuesta }];
        setMessages(updated);
        setTypingIdx(updated.length - 1);
      } else {
        const errorMsg = data?.detail || 'No pude procesar tu pregunta. Intenta de nuevo.';
        setMessages([...newMessages, { role: 'assistant', content: errorMsg }]);
      }
    } catch (err) {
      console.error('NARA Chat error:', err);
      setMessages([...newMessages, { role: 'assistant', content: 'Error de conexion. Verifica que el servidor este activo e intenta de nuevo.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => sendMessage(input.trim());

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleSuggestionClick = (s) => sendMessage(s);

  // ── Voice Mode ──
  const startRecording = async () => {
    try {
      setVoiceError(null);
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const ctx = window.isSecureContext ? 'secure' : 'insecure';
        const loc = window.location?.origin || 'unknown';
        setVoiceError(`Mic no disponible (${ctx}, ${loc})`);
        setVoiceMode(false); voiceActiveRef.current = false;
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      audioContextRef.current = audioCtx;
      analyserRef.current = analyser;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        if (!voiceActiveRef.current) { chunksRef.current = []; return; }
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        if (blob.size < 1000) { if (voiceActiveRef.current) restartRecording(); return; }
        await transcribeAndSend(blob);
      };

      recorder.start(250);
      monitorSilence();
    } catch (err) {
      console.warn('Mic access error:', err);
      setVoiceError(`Error: ${err.message || err.name || 'Permiso denegado'}`);
      setVoiceMode(false); voiceActiveRef.current = false;
    }
  };

  const monitorSilence = () => {
    if (!analyserRef.current) return;
    const data = new Uint8Array(analyserRef.current.frequencyBinCount);
    let silenceStart = null; let speaking = false;
    const check = () => {
      if (!voiceActiveRef.current) return;
      analyserRef.current.getByteFrequencyData(data);
      const avg = data.reduce((a, b) => a + b, 0) / data.length;
      if (avg > 15) { speaking = true; silenceStart = null; }
      else if (speaking && !silenceStart) { silenceStart = Date.now(); }
      if (speaking && silenceStart && Date.now() - silenceStart > 1500) {
        if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
        return;
      }
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  };

  const restartRecording = () => {
    if (!voiceActiveRef.current || !streamRef.current) return;
    try {
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const recorder = new MediaRecorder(streamRef.current, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        if (!voiceActiveRef.current) { chunksRef.current = []; return; }
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        if (blob.size < 1000) { if (voiceActiveRef.current) restartRecording(); return; }
        await transcribeAndSend(blob);
      };
      recorder.start(250);
      monitorSilence();
    } catch (e) { console.warn('Restart recording error:', e); }
  };

  const WHISPER_HALLUCINATIONS = [
    'subtítulos realizados por la comunidad de amara.org', 'subtitulos realizados por la comunidad de amara.org',
    'amara.org', 'gracias por ver el vídeo', 'gracias por ver el video', 'gracias por ver',
    'thanks for watching', 'thank you for watching', 'suscríbete al canal', 'like and subscribe',
    'música', '...', 'you', 'bye',
  ];
  const isWhisperHallucination = (text) => {
    const lower = text.toLowerCase().trim();
    return WHISPER_HALLUCINATIONS.some(h => lower.includes(h)) || lower.length < 3;
  };

  const transcribeAndSend = async (blob) => {
    if (!voiceActiveRef.current) return;
    setVoiceTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'voice.webm');
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_URL}/emr/ia/transcribir-voz`, {
        method: 'POST',
        headers: { 'X-User-Id': userId, ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: formData
      });
      if (!voiceActiveRef.current) return;
      const data = await res.json();
      if (data.texto && data.texto.trim() && !isWhisperHallucination(data.texto)) {
        const msg = data.texto.trim();
        setInput(msg);
        setTimeout(() => { setInput(''); sendMessage(msg); }, 100);
      }
    } catch (err) { console.warn('Transcription error:', err); }
    finally { setVoiceTranscribing(false); if (voiceActiveRef.current) restartRecording(); }
  };

  const stopVoice = () => {
    voiceActiveRef.current = false;
    if (mediaRecorderRef.current?.state === 'recording') try { mediaRecorderRef.current.stop(); } catch (e) { /* ok */ }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (audioContextRef.current) { try { audioContextRef.current.close(); } catch (e) { /* ok */ } audioContextRef.current = null; }
    analyserRef.current = null; mediaRecorderRef.current = null;
  };

  useEffect(() => {
    if (voiceMode) { voiceActiveRef.current = true; startRecording(); }
    else { stopVoice(); }
    return () => stopVoice();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceMode]);

  const toggleVoice = () => setVoiceMode(v => !v);

  const handleClose = () => {
    setOpen(false); setMessages([]); setTypingIdx(-1); setPanelSize('compact');
    if (voiceMode) setVoiceMode(false);
  };

  if (!userId) return null;

  const size = PANEL_SIZES[panelSize];
  const isFullscreen = panelSize === 'fullscreen';
  const isExpanded = panelSize === 'expanded';
  const suggestions = getSuggestions();
  const showSuggestions = messages.length <= 1 && !loading;

  // Entity context indicator for header
  const entityLabel = entityContext
    ? entityContext.type === 'reservacion' ? `Reserva: ${entityContext.data.codigo_reserva || ''}`
    : entityContext.type === 'salida' ? `Salida: ${entityContext.data.fecha || ''}`
    : entityContext.type === 'chatbot' ? entityContext.data.nombre
    : null
    : null;

  return (
    <>
      {/* FAB */}
      {!open && (
        <Tooltip title="NARA Chat (Ctrl+Shift+N)" placement="left">
          <Box
            onClick={handleOpen}
            sx={{
              position: 'fixed', bottom: 20, right: 20, zIndex: 1300,
              width: 44, height: 44, borderRadius: '50%',
              background: theme.gradient,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: `0 4px 20px ${theme.shadow}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '@keyframes pulseGlow': {
                '0%': { boxShadow: `0 4px 20px ${theme.shadow}` },
                '50%': { boxShadow: `0 4px 30px ${theme.shadowHover}, 0 0 20px ${theme.shadow}` },
                '100%': { boxShadow: `0 4px 20px ${theme.shadow}` }
              },
              animation: messages.length > 0 ? 'none' : 'pulseGlow 3s ease-in-out infinite',
              '&:hover': { transform: 'scale(1.1)', boxShadow: `0 6px 28px ${theme.shadowHover}` },
              '&:active': { transform: 'scale(0.95)' }
            }}
          >
            <PsychologyIcon sx={{ color: '#fff', fontSize: 22 }} />
            {messages.length > 0 && (
              <Box sx={{ position: 'absolute', top: 1, right: 1, width: 10, height: 10, borderRadius: '50%', bgcolor: '#fbbf24', border: `2px solid ${theme.secondary}` }} />
            )}
            {isAdmin && (
              <Box sx={{ position: 'absolute', bottom: -2, right: -2, width: 16, height: 16, borderRadius: '50%', background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #0f172a' }}>
                <AdminIcon sx={{ color: '#fff', fontSize: 9 }} />
              </Box>
            )}
          </Box>
        </Tooltip>
      )}

      {/* Chat Panel */}
      {open && (
        <Box sx={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 1300,
          width: isFullscreen ? size.width : { xs: 'calc(100% - 32px)', sm: size.width },
          height: size.height, maxHeight: 'calc(100vh - 48px)', maxWidth: 'calc(100vw - 48px)',
          bgcolor: '#0f172a',
          borderRadius: isFullscreen ? 3 : '16px 16px 4px 16px',
          border: `1px solid ${theme.border}`,
          boxShadow: `0 12px 48px rgba(0,0,0,0.4), 0 0 1px ${theme.border}`,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes slideUp': { from: { opacity: 0, transform: 'translateY(20px) scale(0.95)' }, to: { opacity: 1, transform: 'translateY(0) scale(1)' } },
          animation: 'slideUp 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: theme.barGradient }
        }}>
          {/* Header */}
          <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', gap: 0.5, mr: 0.5, flexShrink: 0 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444' }} />
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.primary }} />
              </Box>
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: theme.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <PsychologyIcon sx={{ color: '#fff', fontSize: 14 }} />
              </Box>
              <Typography sx={{ ...mono, fontSize: 11, color: '#e2e8f0', fontWeight: 600, flexShrink: 0 }}>NARA</Typography>
              {/* Mode chip */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.2, borderRadius: 1, bgcolor: theme.chipBg, border: `1px solid ${theme.chipBorder}`, flexShrink: 0 }}>
                {isAdmin ? <AdminIcon sx={{ fontSize: 10, color: theme.chipColor }} /> : <DoctorIcon sx={{ fontSize: 10, color: theme.chipColor }} />}
                <Typography sx={{ ...mono, fontSize: 9, color: theme.chipColor, fontWeight: 600 }}>{theme.label}</Typography>
              </Box>
              {/* Entity context chip */}
              {entityLabel && (
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.4,
                  px: 0.8, py: 0.2, borderRadius: 1,
                  bgcolor: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.25)',
                  maxWidth: 120, overflow: 'hidden', flexShrink: 1,
                }}>
                  <Typography sx={{ ...mono, fontSize: 9, color: '#fbbf24', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {entityLabel}
                  </Typography>
                </Box>
              )}
              {(isExpanded || isFullscreen) && (
                <Typography sx={{ ...mono, fontSize: 9, color: '#475569', flexShrink: 0 }}>nara@asistente ~ chat</Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
              <Tooltip title={panelSize === 'compact' ? 'Expandir' : panelSize === 'expanded' ? 'Pantalla completa' : 'Compacto'} placement="bottom">
                <IconButton size="small" onClick={cyclePanelSize} sx={{ color: '#64748b', '&:hover': { color: theme.chipColor } }}>
                  {panelSize === 'fullscreen' ? <CollapseIcon sx={{ fontSize: 14 }} /> : <ExpandIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={handleClose} sx={{ color: '#64748b', '&:hover': { color: '#e2e8f0' } }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 } }}>
            {messages.map((msg, idx) => (
              <Box key={idx} sx={{ mb: 1.5, display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                {msg.role === 'assistant' ? (
                  <Box
                    onMouseEnter={() => setHoveredMsg(idx)}
                    onMouseLeave={() => setHoveredMsg(-1)}
                    sx={{ maxWidth: isFullscreen ? '80%' : '92%', position: 'relative' }}
                  >
                    {hoveredMsg === idx && typingIdx !== idx && (
                      <Tooltip title="Copiar" placement="top">
                        <IconButton size="small" onClick={() => copyToClipboard(msg.content)}
                          sx={{ position: 'absolute', top: -4, right: -4, zIndex: 1, bgcolor: 'rgba(30, 41, 59, 0.9)', border: '1px solid rgba(255,255,255,0.1)', width: 22, height: 22, '&:hover': { bgcolor: 'rgba(51, 65, 85, 0.9)' } }}>
                          <CopyIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Box component="span" sx={{ color: theme.primary, fontWeight: 700, ...mono, fontSize: 11.5 }}>{'>'} </Box>
                    {idx === typingIdx ? (
                      <Typography component="span" sx={{ ...mono, fontSize: 11.5, color: '#e2e8f0', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        <ChatTypewriter text={msg.content} onDone={() => setTypingIdx(-1)} />
                      </Typography>
                    ) : (
                      <MarkdownMessage content={msg.content} theme={theme} onNavigate={handleNavigate} />
                    )}
                  </Box>
                ) : (
                  <Box sx={{
                    bgcolor: isAdmin ? 'rgba(139, 92, 246, 0.15)' : 'rgba(99, 102, 241, 0.15)',
                    border: isAdmin ? '1px solid rgba(139, 92, 246, 0.2)' : '1px solid rgba(99, 102, 241, 0.2)',
                    borderRadius: '10px 10px 2px 10px', px: 1.5, py: 0.75, maxWidth: isFullscreen ? '70%' : '85%'
                  }}>
                    <Typography sx={{ ...mono, fontSize: 11.5, color: isAdmin ? '#c4b5fd' : '#c7d2fe', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}

            {loading && (
              <Typography sx={{ ...mono, fontSize: 11.5, color: theme.primary, mb: 1 }}>
                <Box component="span" sx={{ color: theme.secondary, fontWeight: 700 }}>{'>'} </Box>
                <Box component="span" sx={{ '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0 } }, animation: 'blink 1s step-end infinite' }}>...</Box>
              </Typography>
            )}

            {showSuggestions && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5, mb: 0.5 }}>
                {suggestions.map((s, i) => (
                  <Chip key={i} label={s} size="small" onClick={() => handleSuggestionClick(s)}
                    sx={{ ...mono, fontSize: 10, height: 26, bgcolor: 'rgba(255,255,255,0.04)', border: `1px solid ${theme.border}`, color: '#94a3b8', cursor: 'pointer', transition: 'all 0.2s',
                      '&:hover': { bgcolor: theme.chipBg, borderColor: theme.chipBorder, color: theme.chipColor }
                    }}
                  />
                ))}
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Voice error */}
          {voiceError && (
            <Box sx={{ px: 2, py: 0.5, bgcolor: 'rgba(239,68,68,0.15)', flexShrink: 0 }}>
              <Typography sx={{ ...mono, fontSize: 10, color: '#f87171' }}>{voiceError}</Typography>
            </Box>
          )}

          {/* Input */}
          <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid rgba(255,255,255,0.06)', bgcolor: 'rgba(15, 23, 42, 0.8)', flexShrink: 0 }}>
            <TextField
              inputRef={inputRef} fullWidth size="small"
              placeholder={voiceTranscribing ? 'Transcribiendo...' : voiceMode ? 'Escuchando...' : isAdmin ? 'Consulta a NARA (Admin)...' : 'Pregunta a NARA...'}
              value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown} disabled={loading}
              multiline maxRows={isFullscreen ? 6 : 3}
              sx={{
                '& .MuiOutlinedInput-root': { ...mono, fontSize: 12, color: '#e2e8f0', bgcolor: 'rgba(30, 41, 59, 0.6)', borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover fieldset': { borderColor: `${theme.primary}44` }, '&.Mui-focused fieldset': { borderColor: theme.primary, borderWidth: 1 } },
                '& .MuiInputBase-input::placeholder': { color: '#475569', opacity: 1 }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton size="small" onClick={toggleVoice} disabled={voiceTranscribing}
                      sx={{ color: voiceTranscribing ? '#f59e0b' : voiceMode ? '#ef4444' : '#475569',
                        '@keyframes micPulse': { '0%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.5, transform: 'scale(1.2)' }, '100%': { opacity: 1, transform: 'scale(1)' } },
                        animation: voiceMode ? 'micPulse 1.5s ease-in-out infinite' : 'none', '&:hover': { color: voiceMode ? '#f87171' : '#64748b' } }}>
                      {voiceMode ? <MicIcon sx={{ fontSize: 18 }} /> : <MicOffIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSend} disabled={!input.trim() || loading}
                      sx={{ color: input.trim() ? theme.primary : '#334155', '&:hover': { color: theme.chipColor } }}>
                      <SendIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Typography sx={{ ...mono, fontSize: 8.5, color: '#334155', textAlign: 'right', mt: 0.3, mr: 0.5 }}>Ctrl+Shift+N</Typography>
          </Box>
        </Box>
      )}

      <Snackbar open={copySnackbar} autoHideDuration={1500} onClose={() => setCopySnackbar(false)} message="Copiado"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ '& .MuiSnackbarContent-root': { minWidth: 'auto', ...mono, fontSize: 12 } }}
      />
    </>
  );
};

export default NaraChat;
